// src/loaders/plyLoader.js
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader.js'
import {
  createMesh,
  centerAndOrientGeometry,
  mergeAndNormalizeGeometry,
  setupColorAttribute,
  setupWeightAttribute,
} from './loaderUtils'

/**
 * Load PLY to geometry only (parse + center). No merge, repair, or mesh creation.
 * Used by phased loader for worker-based merge.
 * @param {string} pathOrBlobUrl - File path or Blob URL
 * @param {Object} [opts]
 * @param {boolean} [opts.skipOrientation] - Skip standard rotations (checkpoint reloads).
 * @returns {Promise<THREE.BufferGeometry>}
 */
export function loadPLYGeometryOnly(pathOrBlobUrl, opts = {}) {
  return new Promise((resolve, reject) => {
    const loader = new PLYLoader()
    loader.load(
      pathOrBlobUrl,
      (geometry) => {
        centerAndOrientGeometry(geometry, { orient: !opts.skipOrientation })
        resolve(geometry)
      },
      undefined,
      reject
    )
  })
}

/**
 * Load PLY model from a path or Blob URL
 * @param {string} pathOrBlobUrl - File path or Blob URL
 * @param {Object} [opts]
 * @param {boolean} [opts.skipOrientation] - Skip standard rotations (checkpoint reloads).
 * @returns {Promise<THREE.Mesh>}
 */
export function loadPLY(pathOrBlobUrl, opts = {}) {
  return new Promise((resolve, reject) => {
    const loader = new PLYLoader()
    loader.load(
      pathOrBlobUrl,
      (geometry) => {
        centerAndOrientGeometry(geometry, { orient: !opts.skipOrientation })

        // Merge vertices and compute normals
        // **CRITICAL**: mergeVertices() returns a new indexed geometry object
        // We must capture the return value to ensure proper indexing across all loaders
        geometry = mergeAndNormalizeGeometry(geometry, 1e-4, 'PLY')

        // Setup color attribute
        setupColorAttribute(geometry)

        // Setup weight attribute
        setupWeightAttribute(geometry)

        // Create mesh with standard material
        const mesh = createMesh(geometry, 'PLYMesh')
        resolve(mesh)
      },
      undefined,
      reject
    )
  })
}
